import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, Legend, Cell, ResponsiveContainer } from 'recharts';
import "../../common/css/component.css";
import "./monthlyInvestmentChart.css";
import { formatCurrency } from '../../common/util/dataFormatterUtils';

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function calculateMonthlyInvestments(investmentData) {
    const monthYearMap = new Map();
    let startDate = new Date('01/01/2022');
    let endDate = new Date();
    endDate.setFullYear(endDate.getFullYear(), 11, 31);

    investmentData.forEach(({investmentDate, investmentAmount}) => {
        const date = new Date(investmentDate);
        const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;

        startDate = !startDate || date < startDate ? new Date(date.getFullYear(), date.getMonth(), 1) : startDate;
        monthYearMap.set(monthYear, (monthYearMap.get(monthYear) || 0) + investmentAmount);
    });

    let currentMonthYear = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    while (currentMonthYear <= endDate) {
        const monthYear = `${currentMonthYear.getMonth() + 1}/${currentMonthYear.getFullYear()}`;
        if (!monthYearMap.has(monthYear)) monthYearMap.set(monthYear, 0);
        currentMonthYear.setMonth(currentMonthYear.getMonth() + 1);
    }

    const monthlyInvestmentData = Array.from(monthYearMap, ([monthYear, total]) => {
        const [month, year] = monthYear.split("/");
        return { year, month, value: total }; // data structure with year, month, value
    });

    return monthlyInvestmentData.sort((a, b) => new Date(a.year, a.month - 1) - new Date(b.year, b.month - 1));
}

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const { month, year, value } = payload[0].payload;
        const monthName = monthNames[parseInt(month, 10) - 1];
        const totalInvestment = formatCurrency(value);
        return (
            <div className="custom-tooltip">
                <p><span className="bold-label">Month:</span> {`${monthName} ${year}`}</p>
                <p><span className="bold-label">Total Investment:</span> {totalInvestment}</p>
            </div>
        );
    }
    return null;
};

const MonthlyInvestmentChart = ({ investmentData }) => {
    const rootStyles = getComputedStyle(document.documentElement);
    const color = rootStyles.getPropertyValue("--chart-color-6").trim();
    const color_0 = rootStyles.getPropertyValue("--chart-color-inactive").trim();

    const data = calculateMonthlyInvestments(investmentData);
    const dataByYear = data.reduce((groups, item) => {
        const {year} = item;
        if (!groups[year]) groups[year] = [];
        groups[year].push(item);
        return groups;
    }, {});
    const years = Object.keys(dataByYear);

    return (
        <div className="component-container-no-border">
            {years.map(year => (
                <ResponsiveContainer width="100%" height={95}>
                    <ScatterChart margin={{ top: 20, right: 20, bottom: 10, left: 10 }}>
                        <XAxis dataKey="month" name="Month" type="category" interval={0}
                            tick={year === new Date().getFullYear().toString() ? 
                                { dy: 15, fill: 'black', fontSize: '14px', textAnchor: 'middle'} : 
                                { fontSize: 0 }
                            }
                            tickLine={{ transform: 'translate(0, -6)' }}
                            tickFormatter={month => monthNames[month - 1]}
                        />
                        <YAxis dataKey="year" name="Year" type="number" height={10} width={80} tick={false} 
                            tickLine={false} axisLine={false} label={{ value: year, position: 'insideRight', fill: 'black'}}
                        />
                        <ZAxis dataKey="value" name="Total Investment" range={[64, 1536]} />
                        <Scatter name="A school" data={dataByYear[year]}>
                            {dataByYear[year].map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.value > 0 ? color : color_0} />
                            ))}
                        </Scatter>
                        <Tooltip content={<CustomTooltip />} />
                    </ScatterChart>
                </ResponsiveContainer>
            ))}
        </div>
    );
};

export default MonthlyInvestmentChart;
